import { useState } from "react";
import type { UseProgressResult } from "@/core/hooks/useProgress";
import { modulesByLevel, type Course, type CourseModule } from "@/core/types";

/**
 * Was die Fortschrittsanzeigen über den Stand wissen müssen. Der Hook des
 * Panels liefert das ebenso wie die Browser-Ansicht, die alle Kurse auf
 * einmal liest.
 */
export type ProgressLookup = Pick<UseProgressResult, "completedInLesson" | "isStepDone">;

/** Erledigte und alle Schritte eines Moduls. */
export function moduleSteps(module: CourseModule, progress: ProgressLookup) {
  let done = 0;
  let total = 0;
  for (const lesson of module.lessons) {
    total += lesson.steps.length;
    done += progress.completedInLesson(
      module.id,
      lesson.id,
      lesson.steps.map((step) => step.id),
    );
  }
  return { done, total };
}

interface StatusRingProps {
  /** Anteil zwischen 0 und 1. */
  fraction: number;
  size?: number;
}

/**
 * Status als Ring: leer, angefangen (Bogen in der Host-Farbe) oder fertig
 * (gefüllt, mit Haken). Rein dekorativ – der Text daneben sagt dasselbe.
 */
export function StatusRing({ fraction, size = 20 }: StatusRingProps) {
  const value = Math.max(0, Math.min(1, fraction));
  const stroke = size >= 20 ? 2 : 1.75;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const done = value >= 1;

  return (
    <svg
      className={`ring ${done ? "ring--done" : ""}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <circle className="ring__track" cx={center} cy={center} r={radius} strokeWidth={stroke} />
      <circle
        className="ring__arc"
        cx={center}
        cy={center}
        r={radius}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - value)}
        transform={`rotate(-90 ${center} ${center})`}
      />
      {done ? (
        <path
          className="ring__check"
          d={`M${size * 0.3} ${size * 0.52} L${size * 0.44} ${size * 0.66} L${size * 0.71} ${size * 0.36}`}
          pathLength={1}
        />
      ) : null}
    </svg>
  );
}

interface CurriculumBandProps {
  course: Course;
  progress: ProgressLookup;
  onSelectModule(moduleId: string): void;
}

/**
 * Das Lehrplan-Band: ein Segment pro Modul, so breit wie das Modul Schritte
 * hat, gefüllt mit dem erledigten Anteil. Grundlagen und Aufbau stehen als
 * zwei Gruppen nebeneinander. Zeigen oder Fokussieren nennt das Modul,
 * Klicken öffnet es in der Liste darunter.
 */
export function CurriculumBand({ course, progress, onSelectModule }: CurriculumBandProps) {
  const groups = modulesByLevel(course);
  const [hovered, setHovered] = useState<string | null>(null);

  const stats = new Map(course.modules.map((module) => [module.id, moduleSteps(module, progress)]));
  const hoveredModule = course.modules.find((module) => module.id === hovered);
  const hoveredStats = hoveredModule ? stats.get(hoveredModule.id) : undefined;

  return (
    <div className="band" onMouseLeave={() => setHovered(null)}>
      <div className="band__track">
        {groups.map((group) => {
          const groupTotal = group.modules.reduce(
            (sum, module) => sum + (stats.get(module.id)?.total ?? 0),
            0,
          );
          return (
            <div key={group.level} className="band__group" style={{ flexGrow: groupTotal }}>
              {group.modules.map((module) => {
                const { done, total } = stats.get(module.id) ?? { done: 0, total: 1 };
                return (
                  <button
                    key={module.id}
                    type="button"
                    className={`band__segment ${hovered === module.id ? "is-hovered" : ""}`}
                    style={{ flexGrow: total }}
                    aria-label={`${module.title}: ${done} von ${total} Schritten erledigt`}
                    onMouseEnter={() => setHovered(module.id)}
                    onFocus={() => setHovered(module.id)}
                    onBlur={() => setHovered(null)}
                    onClick={() => onSelectModule(module.id)}
                  >
                    <span className="band__bar">
                      <span
                        className="band__fill"
                        style={{ transform: `scaleX(${total === 0 ? 0 : done / total})` }}
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="band__readout" aria-hidden="true">
        {hoveredModule && hoveredStats ? (
          <p className="band__detail" key={hoveredModule.id}>
            <span className="band__detail-title">{hoveredModule.title}</span>
            <span className="num">
              {hoveredStats.done}/{hoveredStats.total}
            </span>
          </p>
        ) : (
          groups.map((group) => {
            const groupTotal = group.modules.reduce(
              (sum, module) => sum + (stats.get(module.id)?.total ?? 0),
              0,
            );
            return (
              <span key={group.level} className="band__label" style={{ flexGrow: groupTotal }}>
                {group.level}
              </span>
            );
          })
        )}
      </div>
    </div>
  );
}

interface StepTrackProps {
  steps: Array<{ id: string; title: string; done: boolean }>;
  /** Index des offenen Schritts; `-1` oder größer als die Liste: keiner. */
  current: number;
  onSelect?: (index: number) => void;
}

/** Die Schritte einer Lektion als Band – optional klickbar. */
export function StepTrack({ steps, current, onSelect }: StepTrackProps) {
  return (
    <ol className={`steptrack ${onSelect ? "steptrack--interactive" : ""}`}>
      {steps.map((step, index) => {
        const className = [
          "steptrack__item",
          step.done ? "is-done" : "",
          index === current ? "is-current" : "",
        ].join(" ");
        const bar = (
          <span className="steptrack__bar">
            <span className="steptrack__fill" />
          </span>
        );
        return (
          <li key={step.id} className={className}>
            {onSelect ? (
              <button
                type="button"
                className="steptrack__button"
                onClick={() => onSelect(index)}
                aria-current={index === current ? "step" : undefined}
                aria-label={`Schritt ${index + 1}: ${step.title}${step.done ? " (erledigt)" : ""}`}
                title={step.title}
              >
                {bar}
              </button>
            ) : (
              bar
            )}
          </li>
        );
      })}
    </ol>
  );
}
